import {
  FlexBox,
  JoonUIColor,
  SPACING,
  TextArea,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"

import TextArrowButton from "../../../components/coaching/TextArrowButton"
import PageLoader from "../../../components/loading/PageLoader"
import {
  useCoachPostSelectionQuestionnaireQuery,
  useUserQuery,
} from "../../../networking/queries"
import {
  QuestionAnswerMappingInterface,
  QuestionInterface,
  QuestionnairesInterface,
} from "../../../types"
import { CoachingStep } from "../../../types/coachingGroups"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import SmallCoachCard from "../SmallCoachCard"
import { useCoachingStore } from "../useCoachingStore"
import useIsFromMOF from "../useIsFromMOF"

interface PostSelectionQuestionnaireProps {
  onConfirm: (
    parentId: number,
    coachQuestionnaire: QuestionnairesInterface,
    questionAnswerMapping: QuestionAnswerMappingInterface
  ) => void
}

const PostSelectionQuestionnaire = ({
  onConfirm,
}: PostSelectionQuestionnaireProps) => {
  const { t } = useTranslation()
  const {
    selectedCoach,
    setStep,
    postSelectionQuestionAnswerMapping,
    setPostSelectionQuestionAnswerMapping,
  } = useCoachingStore()
  const { user } = useUserQuery()
  const isFromMOF = useIsFromMOF()
  const { data: questionnaire, isLoading } =
    useCoachPostSelectionQuestionnaireQuery()
  const questions: QuestionInterface[] | [] = questionnaire?.questions || []

  const onSubmitFreeformAnswerClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.MARKETING_POST_SELECTION_SUBMIT, {
      is_tof: !isFromMOF,
    })
    if (user && questionnaire)
      onConfirm(user.id, questionnaire, postSelectionQuestionAnswerMapping)
    setStep(CoachingStep.PLAN_SELECT)
  }

  if (isLoading || !selectedCoach) {
    return <PageLoader />
  }

  return (
    <FlexBox
      justify="flex-start"
      align="center"
      direction="column"
      gap={SPACING.space6}
      style={{
        minHeight: "70vh",
        width: "min(450px, 90vw)",
        paddingTop: "20px",
      }}
    >
      <Typography variant="bodyBold" color={JoonUIColor.text.primary}>
        {t("COACHING_POSTSEL_GREAT_CHOICE")}
      </Typography>
      <SmallCoachCard coachCardContent={selectedCoach} />
      <Typography variant="body" color={JoonUIColor.text.primary}>
        {t("COACHING_POSTSEL_INTRO", { name: selectedCoach.user.name })}
      </Typography>
      <FlexBox
        key={questions[0].id}
        direction="column"
        align="flex-start"
        gap={SPACING.space3}
        fullWidth
        style={{ marginBottom: SPACING.space4 }}
      >
        <Typography variant="h2" color={JoonUIColor.text.primary}>
          {questions[0].question}
        </Typography>
        <TextArea
          placeholder={t("COACHING_FREEFORM_PLACEHOLDER")}
          value={
            postSelectionQuestionAnswerMapping[questions[0].id]?.toString() ||
            ""
          }
          onChange={(e) =>
            setPostSelectionQuestionAnswerMapping({
              [questions[0].id]: e.target.value,
            })
          }
          name="freeformAnswer"
          maxLength={255}
          showCounter={true}
          fullWidth
        />
      </FlexBox>
      <TextArrowButton
        text={t("QUESTIONNAIRE_NEXT")}
        onClick={onSubmitFreeformAnswerClick}
        disabled={
          !postSelectionQuestionAnswerMapping[questions[0].id] ||
          postSelectionQuestionAnswerMapping[questions[0].id]?.toString()
            .length === 0
        }
        style={{
          width: "min(450px, calc(98% - 40px))",
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: "7dvh",
        }}
      />
    </FlexBox>
  )
}

export default PostSelectionQuestionnaire
